import React, { useEffect, useState } from 'react';
import {
  Box, Card, Chip, Paper, Rating, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon, Star as StarIcon,
} from '@mui/icons-material';
import axios from '../api';
import merchantService from '../services/merchant.service';
import { useAuth } from '../contexts/AuthContext';
import PaginationControls from '../components/PaginationControls';

export default function Reviews() {
  const { user } = useAuth();
  const isMerchant = user?.role === 'merchant';
  const [orders, setOrders] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ avgRating: number; fiveStars: number; oneStars: number } | null>(null);

  useEffect(() => {
    fetchReviews();
  }, [page, rowsPerPage]);

  const fetchReviews = async () => {
    try {
      let response;
      if (isMerchant) {
        response = await merchantService.getReviews({ page, limit: rowsPerPage });
      } else {
        response = await axios.get('/api/operations/reviews', { params: { page, limit: rowsPerPage } });
      }
      setOrders(response.data?.reviews || response.reviews || []);
      setTotal(response.data?.total || response.total || 0);
      setServerStats(response.data?.stats || response.stats || null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setLoading(false);
    }
  };

  const ordersWithReviews = orders.filter(o => o.review).map(o => {
    // For merchants, get the surprise box name from items
    const merchantItems = isMerchant
      ? o.items?.filter((item: any) => item.business?._id || item.business) || []
      : [];

    return {
      _id: o._id,
      orderNumber: o.orderNumber,
      customer: o.customer?.name || 'Unknown',
      merchant: o.business?.name || o.items?.[0]?.business?.name || o.menuItems?.[0]?.business?.name || 'Unknown',
      surpriseBoxes: merchantItems.map((item: any) => item.surpriseBox?.name).filter(Boolean),
      rating: o.review?.rating || 0,
      comment: o.review?.comment || '',
      date: o.review?.createdAt || o.createdAt,
    };
  });

  const filteredReviews = ordersWithReviews.filter(review =>
    review.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.merchant?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.comment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Prefer the backend's platform-wide aggregates (computed over every
  // review, not just the current page) so the cards don't look frozen as
  // you paginate or search. Fall back to page-local numbers only if the
  // server didn't send stats (e.g. merchant endpoint).
  const stats = serverStats
    ? {
        total,
        avgRating: serverStats.avgRating ? serverStats.avgRating.toFixed(1) : 0,
        fiveStars: serverStats.fiveStars,
        oneStars: serverStats.oneStars,
      }
    : {
        total: ordersWithReviews.length,
        avgRating: ordersWithReviews.length > 0
          ? (ordersWithReviews.reduce((sum, r) => sum + r.rating, 0) / ordersWithReviews.length).toFixed(1)
          : 0,
        fiveStars: ordersWithReviews.filter(r => r.rating === 5).length,
        oneStars: ordersWithReviews.filter(r => r.rating === 1).length,
      };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isMerchant ? 'My Reviews' : 'Reviews & Reports'}
        </Typography>
        <Typography color="text.secondary">
          {isMerchant
            ? 'See what customers are saying about your products'
            : 'Monitor customer reviews and feedback.'}
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Reviews</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <StarIcon sx={{ color: 'warning.main' }} />
          </Box>
          <Typography color="text.secondary" variant="body2">Average Rating</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.avgRating}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">5 Star Reviews</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.fiveStars}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">1 Star Reviews</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.oneStars}</Typography>
        </Paper>
      </Box>

      <Card>
        <Box sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search reviews by customer, merchant, or comment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Order</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                {isMerchant && <TableCell><strong>Product</strong></TableCell>}
                {!isMerchant && <TableCell><strong>Merchant</strong></TableCell>}
                <TableCell><strong>Rating</strong></TableCell>
                <TableCell><strong>Comment</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={isMerchant ? 6 : 6} align="center" sx={{ py: 5 }}>
                    <Typography>Loading reviews...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredReviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMerchant ? 6 : 6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No reviews found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredReviews.map((review) => (
                <TableRow key={review._id} hover>
                  <TableCell><Typography sx={{ fontWeight: 600 }}>{review.orderNumber}</Typography></TableCell>
                  <TableCell>{review.customer}</TableCell>
                  {isMerchant && (
                    <TableCell>
                      {review.surpriseBoxes.length > 0 ? (
                        <Box>
                          {review.surpriseBoxes.map((box: string, idx: number) => (
                            <Chip
                              key={idx}
                              label={box}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No items</Typography>
                      )}
                    </TableCell>
                  )}
                  {!isMerchant && <TableCell>{review.merchant}</TableCell>}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={review.rating} readOnly size="small" />
                      <Typography variant="body2">{review.rating}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {review.comment ? (
                      review.comment.length > 50 ? `${review.comment.substring(0, 50)}...` : review.comment
                    ) : (
                      <Typography variant="body2" color="text.secondary">No comment</Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(review.date).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />
    </Box>
  );
}
